<template>
  <TresGroup>
    <primitive
      ref="boxRef"
      :object="scene"
      :scale="[0, 0, 0]"
      :rotation="[Math.PI / 2, Math.PI / 2, 0]"
      :position="[0, 0, -100]"
      @click="handleRedirect"
      @pointer-enter="onPointerEnter"
      @pointer-leave="onPointerLeave"
    />
  </TresGroup>
</template>

<script setup lang="ts">
import { useThrottleFn } from '@vueuse/core'
import gsap from 'gsap'
import * as THREE from 'three'
import { useTexture } from '@tresjs/core'
import { MeshStandardMaterial, MeshPhysicalMaterial, type Scene, Mesh, ShaderMaterial } from 'three'
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js'

const { onBeforeRender } = useLoop()
const { width } = useWindowSize()
const boxRef = ref<THREE.Object3D | null>(null)
const workAnimation = useState('workAnimation')

interface Props {
  id?: number
  title?: string
  texture: string
  scene: Scene
  path: string
  delay?: number
}

const props = withDefaults(defineProps<Props>(), {
  id: 0,
  title: 'Title',
  texture: 'https://www.datocms-assets.com/142360/1731101051-frame-1.png',
  scene: null,
  delay: 0
})

const pbrTexture = await useTexture({
  map: props.texture,
  displacementMap: props.texture,
  roughnessMap: props.texture,
  normalMap: props.texture,
  aoMap: props.texture,
  metalnessMap: props.texture,
  matcap: props.texture,
  alphaMap: props.texture
})

const badTVMaterial = new ShaderMaterial({
  uniforms: {
    tDiffuse: { type: 't', value: null }, // Texture map to be set externally
    time: { type: 'f', value: 0.0 },
    distortion: { type: 'f', value: 0.1 },
    distortion2: { type: 'f', value: 3.0 },
    speed: { type: 'f', value: 3.0 },
    rollSpeed: { type: 'f', value: 0.1 },
    amount: { type: 'f', value: 0.2 },
    size: { type: 'f', value: 3 },
    lineBrightness: { type: 'f', value: 0.1 },
    brightness: { type: 'f', value: 0.8 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
  uniform sampler2D tDiffuse;
  uniform float time;
  uniform float distortion;
  uniform float distortion2;
  uniform float speed;
  uniform float rollSpeed;
  uniform float amount;
  uniform float size;
  uniform float lineBrightness;
  uniform float brightness; // Brightness control
  varying vec2 vUv;

  // Start Ashima 2D Simplex Noise
  vec3 mod289(vec3 x) {
    return x - floor(x * (1.0 / 289.0)) * 289.0;
  }

  vec2 mod289(vec2 x) {
    return x - floor(x * (1.0 / 289.0)) * 289.0;
  }

  vec3 permute(vec3 x) {
    return mod289(((x * 34.0) + 1.0) * x);
  }

  float snoise(vec2 v) {
    const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
    vec2 i = floor(v + dot(v, C.yy));
    vec2 x0 = v - i + dot(i, C.xx);

    vec2 i1;
    i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec4 x12 = x0.xyxy + C.xxzz;
    x12.xy -= i1;

    i = mod289(i);
    vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));

    vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
    m = m * m;
    m = m * m;

    vec3 x = 2.0 * fract(p * C.www) - 1.0;
    vec3 h = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;

    m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);

    vec3 g;
    g.x = a0.x * x0.x + h.x * x0.y;
    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
    return 130.0 * dot(m, g);
  }
  // End Ashima 2D Simplex Noise

  float rand(vec2 co) {
    return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
  }

  void main() {
    vec2 p = vUv;

    // Apply a rotation matrix to the texture coordinates
    float angle = radians(-135.0); // Rotate by 45 degrees
    mat2 rotation = mat2(cos(angle), -sin(angle), sin(angle), cos(angle));
    p = rotation * (p - 0.5) + 0.5; // Rotate around the center (0.5, 0.5)

    // Scale the texture coordinates
    float scale = 1.3; // Increase size by 50%
    p = (p - 0.3) / scale + 0.3;

    float ty = time * speed;
    float yt = p.y - ty;

    // Smooth distortion
    float offset = snoise(vec2(yt * 3.0, 0.0)) * 0.2;

    // Boost distortion
    offset = offset * distortion * offset * distortion * offset;

    // Add fine-grain distortion
    offset += snoise(vec2(yt * 50.0, 0.0)) * distortion2 * 0.001;

    // Combine distortion on X with roll on Y (reverse roll direction)
    vec4 color = texture2D(tDiffuse, vec2(fract(p.x + offset), fract(p.y)));

    // Static noise effect
    float xs = floor(gl_FragCoord.x / size);
    float ys = floor(gl_FragCoord.y / size);
    vec4 snow = vec4(rand(vec2(xs * time, ys * time)) * amount);

    // Add colored horizontal lines with vertical movement
    float lineFrequency = 30.0; // Number of lines across the screen
    float lineIntensity = lineBrightness;  // How bright the lines are
    float lineThickness = 0.2; // Thickness of the lines

    // Add time-based vertical offset to lines
    float verticalOffset = fract(p.y + time * rollSpeed);
    float linePattern = step(lineThickness, abs(fract(verticalOffset * lineFrequency) - 0.5));
    vec4 customLines = vec4(
      0.035 * lineIntensity * (1.0 - linePattern), // Red
      0.941 * lineIntensity * (1.0 - linePattern), // Green
      1.0 * lineIntensity * (1.0 - linePattern),   // Blue
      1.0
    );

    // Create semi-transparent linear gradient overlay
    vec3 gradientTopColor = vec3(1.0, 0.411, 0.906); // FF69E7
    vec3 gradientBottomColor = vec3(0.035, 0.941, 1.0); // 09F0FF
    float gradientAlpha = 0.5; // Transparency
    vec3 gradientColor = mix(gradientBottomColor, gradientTopColor, p.y); // Interpolate based on Y
    vec4 gradientOverlay = vec4(gradientColor, gradientAlpha);

    // Combine distortion, static noise, horizontal lines, and gradient
    vec4 finalColor = mix(color + snow + customLines, gradientOverlay, gradientAlpha);

    // Apply brightness control
    gl_FragColor = finalColor * brightness;
  }
`
})

if (pbrTexture.map) {
  pbrTexture.map.repeat.set(-0.5, 0.5)
  pbrTexture.map.center.set(0.5, 0.5)
  pbrTexture.map.rotation = -(Math.PI / 1.35)

  pbrTexture.map.generateMipmaps = false
  pbrTexture.map.minFilter = THREE.LinearFilter
  pbrTexture.map.magFilter = THREE.LinearFilter
  pbrTexture.map.needsUpdate = true
}

if (badTVMaterial.uniforms.tDiffuse) {
  badTVMaterial.uniforms.tDiffuse.value = pbrTexture.map
}

const glassMaterial = new MeshStandardMaterial({
  map: pbrTexture.map,
  metalness: 1,
  roughness: 1
})

const frameMaterial = new MeshPhysicalMaterial({
  color: 0xffffff,
  metalness: 0.8,
  roughness: 0.2
})

const scene = props.scene.clone()

scene.traverse((child) => {
  if (child.isMesh) {
    if (child.name !== 'Frame') {
      child.name = 'Image'
      child.material.dispose()
      child.material = badTVMaterial
    } else {
      child.material.dispose()
      child.material = frameMaterial
    }
  }
})

// Function to load and create 3D text
async function createText(title: string) {
  const fontLoader = new FontLoader()
  const font = await new Promise((resolve) => {
    fontLoader.load('https://threejs.org/examples/fonts/helvetiker_bold.typeface.json', resolve)
  })

  const textGeometry = new TextGeometry(title, {
    font,
    size: width.value > 768 ? 0.2 : 0.35,
    depth: 0,
    bevelEnabled: false
  })

  const textMaterial = new MeshStandardMaterial({
    color: 0xffffff,
    emissive: 0xffffff,
    emissiveIntensity: 0.5
  })
  const textMesh = new Mesh(textGeometry, textMaterial)

  textGeometry.center()

  textMesh.rotation.x = -(Math.PI / 2)

  return textMesh
}

// Add title text to the scene
createText(props.title).then((textMesh) => {
  // Add text mesh to the scene
  scene.add(textMesh)
  // Adjust the position of the text (move it slightly in the Z axis to avoid overlap with the texture)
  textMesh.position.set(0, 0.4, 0) // Adjust the Z position to ensure it is in front of or behind the texture
})

const router = useRouter()

function handleRedirect() {
  router.push({ path: usePathByType('work', props.path as string) })
}

const throttledOnPointerEnter = useThrottleFn(function (event: { object: THREE.Object3D }) {
  const hoveredObject = event.object

  if (hoveredObject.name === 'Image') {
    const frame = scene.getObjectByName('Frame') as THREE.Object3D

    if (frame) {
      gsap.to(badTVMaterial.uniforms.amount, {
        value: 0.0,
        duration: 0.3,
        ease: 'power2.out'
      })
      gsap.to(badTVMaterial.uniforms.brightness, {
        value: 1.0,
        duration: 0.3,
        ease: 'power2.out'
      })
      gsap.to(badTVMaterial.uniforms.distortion, {
        value: 0.0,
        duration: 0,
        ease: 'power2.out'
      })
      gsap.to(badTVMaterial.uniforms.distortion2, {
        value: 0.0,
        duration: 0,
        ease: 'power2.out'
      })
      gsap.to(badTVMaterial.uniforms.lineBrightness, {
        value: 0.0,
        duration: 0.1,
        ease: 'power2.out'
      })
      gsap.to(frame.scale, {
        x: 1.1,
        y: 1.1,
        z: 1.1,
        duration: 0.4,
        ease: 'power2.out'
      })
      gsap.to(hoveredObject.scale, {
        x: 1.3,
        y: 1.3,
        z: 1.3,
        duration: 0.4,
        ease: 'power2.out'
      })
    }
  }
}, 100)

function onPointerEnter(event: { object: THREE.Object3D }) {
  document.body.classList.add('cursor-pointer')
  throttledOnPointerEnter(event)
}

function onPointerLeave(event: { object: THREE.Object3D }) {
  const hoveredObject = event.object
  document.body.classList.remove('cursor-pointer')

  if (hoveredObject.name === 'Image') {
    const frame = scene.getObjectByName('Frame') as THREE.Object3D

    if (frame) {
      gsap.to(badTVMaterial.uniforms.amount, {
        value: 0.2,
        duration: 0.3,
        ease: 'power2.in'
      })
      gsap.to(badTVMaterial.uniforms.brightness, {
        value: 0.8,
        duration: 0.3,
        ease: 'power2.in'
      })
      gsap.to(badTVMaterial.uniforms.distortion, {
        value: 0.1,
        duration: 0.3,
        ease: 'power2.in'
      })
      gsap.to(badTVMaterial.uniforms.distortion2, {
        value: 3.0,
        duration: 0.3,
        ease: 'power2.in'
      })
      gsap.to(badTVMaterial.uniforms.lineBrightness, {
        value: 0.1,
        duration: 0.3,
        ease: 'power2.in'
      })
      gsap.to(frame.scale, {
        x: 1,
        y: 1,
        z: 1,
        duration: 0.4,
        ease: 'power2.inOut'
      })
      gsap.to(hoveredObject.scale, {
        x: 1.1,
        y: 1.1,
        z: 1.1,
        duration: 0.4,
        ease: 'power2.inOut'
      })
    }
  }
}

function startWorkItemAnimation() {
  badTVMaterial.uniforms.lineBrightness.value = 4.0
  if (workAnimation.value) {
    gsap.to(boxRef.value!.position, {
      z: 0,
      duration: 1.0,
      ease: 'power3.out',
      delay: props.delay
    })
    gsap.to(badTVMaterial.uniforms.lineBrightness, {
      value: 0.1,
      duration: 1.3,
      ease: 'power3.out',
      delay: props.delay
    })
    gsap.to(boxRef.value!.rotation, {
      y: 0,
      duration: 1.2,
      ease: 'power3.out',
      delay: props.delay
    })
    gsap.to(boxRef.value!.scale, {
      x: 1.5,
      y: 1.5,
      z: 1.5,
      duration: 1.0,
      ease: 'power3.out',
      delay: props.delay
    })
  }
}

watch(
  () => workAnimation.value,
  (value) => {
    if (value) {
      startWorkItemAnimation()
    }
  }
)

onMounted(() => {
  startWorkItemAnimation()
})

onUnmounted(() => {
  glassMaterial.dispose()
  badTVMaterial.dispose()
  frameMaterial.dispose()
})

onBeforeRender(() => {
  if (badTVMaterial.uniforms.time) {
    badTVMaterial.uniforms.time.value += 0.01
  }
})
</script>
